import { useDashboardReview } from '../TasksOverview/hooks/useDashboardReview'
import { createPortal } from 'react-dom'
import { useCommentComposerPortal } from './useCommentComposerPortal'
import { Box, Flex, Text } from '@chakra-ui/core'
import LocaleText from 'components/LocaleText'
import colors from 'constants/colors'
import useComments from 'hooks/useComments'
import { isEmpty } from 'lodash'
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { getCurrentEpoch } from 'utils'
import Comment, { IExecutionReply } from 'components/Comments/Comment'
import CommentsContentLoader from 'components/Comments/CommentsContentLoader'
import { AIAnalysisCard } from '../TasksOverview/components/AIAnalysisCard'
import { useExecutionText } from '../TasksOverview/hooks/useExecutionText'
import { Button, Chip } from 'components/ui'
import { MessageSquare, Send } from 'lucide-react'
import { AI_USER_ID } from 'redux/actions/comments'
import CommentInput from 'components/Comments/CommentInput'
import { ExecutionCommentComposer, ExecutionCommentDraft } from './ExecutionCommentComposer'
import { IComment, IHandleCreateComment } from 'constants/interfaces'
import { setCommentsCounter, setViewedCommentsIds, updateViews } from 'redux/actions/comments'
import { ImageEditorModal } from 'components/CommonComponents/ImageEditor/ImageEditorModal'
import { PreviewFile } from 'config/types'
import { scrollReplyIntoView } from './scrollReplyIntoView'
import { useCommentAnnotationSource } from './useCommentAnnotationSource'
import { getAiScorePresentation } from '../model/aiScore'

interface IProps {
  splitView?: boolean
  composerTarget?: HTMLDivElement | null
  onCommentOverlayOpenChange?: (open: boolean) => void
  analysisLoading?: boolean
  aiScore?: number
  oid: string
  rg_id: string
  objectType: number
  parentCid: string
  tid: string
  created_by: string
  result_context_id: string
  mediaIndex: number
  recurrence_ts?: string
  result_level: string
  isMediaLoading: boolean
  /** When provided, opens the ImageEditorModal pre-loaded with this URL so the
   *  user can annotate and send it as a comment. Reset to undefined after use. */
  imageToComment?: string
  /** Called when the annotation request is accepted so the parent can clear it. */
  onImageToCommentConsumed?: () => void
  isAIComment: boolean
  targetParentCommentId?: string | null
  targetCommentId?: string | null
}

const CommentSection: React.FC<IProps> = ({
  splitView = false,
  composerTarget,
  onCommentOverlayOpenChange,
  analysisLoading,
  aiScore,
  oid,
  objectType,
  rg_id,
  parentCid,
  tid,
  result_context_id,
  created_by,
  recurrence_ts,
  result_level,
  isMediaLoading,
  imageToComment,
  onImageToCommentConsumed,
  isAIComment,
  targetParentCommentId,
  targetCommentId,
}) => {
  const review = useDashboardReview()
  const t = useExecutionText()
  const dispatch = useDispatch()

  const keyForSave = `${oid}-${parentCid}`

  const {
    fetchComments,
    comments,
    sendComment,
    sendCommentLoading,
    commentsCounter,
    fetchCommentsLoading,
    isLoadMore,
  } = useComments(oid, false, keyForSave)

  const [draftResets, setDraftResets] = useState<Record<string, number>>({})
  const [replyTarget, setReplyTarget] = useState<IExecutionReply | null>(null)
  const [composerOverlayOpen, setComposerOverlayOpen] = useState(false)
  const [editorOpen, setEditorOpen] = useState(false)
  const [editorImages, setEditorImages] = useState<string[]>([])
  const [editorCommentText, setEditorCommentText] = useState('')
  const [offset, setOffset] = useState(0)
  const [limit, setLimit] = useState(6)
  const [updatedCommentsCounter, setUpdatedCommentsCounter] = useState(commentsCounter)
  const [resolvedTargetWindowId, setResolvedTargetWindowId] = useState<string | null>(null)

  const scrollRef = useRef<HTMLDivElement>(null)
  const composerDockRef = useRef<HTMLDivElement>(null)
  const composerHost = useCommentComposerPortal(composerDockRef, composerTarget, splitView)
  const localDrafts = useRef<Record<string, ExecutionCommentDraft>>({})
  const drafts = review?.commentDrafts || localDrafts

  const {
    sendComment: sendReply,
    sendCommentLoading: sendingReply,
    commentsCounter: repliesCount,
  } = useComments(replyTarget?.cid || '', true)

  const resetAnnotation = useCallback(() => {
    setEditorOpen(false)
    setEditorImages([])
    setEditorCommentText('')
  }, [])

  const openAnnotation = useCallback((url?: string) => {
    setEditorImages(url ? [url] : [])
    setEditorCommentText('')
    setEditorOpen(true)
  }, [])

  const releaseAnnotationSource = useCommentAnnotationSource({
    source: imageToComment,
    scope: keyForSave,
    onConsumed: onImageToCommentConsumed,
    onReady: openAnnotation,
    onReset: resetAnnotation,
  })

  const getComments = useCallback(async () => {
    let timestampFromToGetComments = getCurrentEpoch()

    if (comments.length > 0) {
      timestampFromToGetComments = comments?.[0]?.created_at_ts
    }

    await fetchComments({ id: oid, limit, created_ts: timestampFromToGetComments, parentCID: parentCid, keyForSave })

    if (!isEmpty(comments)) {
      setOffset(offset + limit)
      setLimit(5)
    }
    // eslint-disable-next-line
  }, [fetchComments, limit, offset, parentCid])

  const handleCreateComment = async ({ comment, images, videos, audios, files }: IHandleCreateComment) => {
    if (splitView && replyTarget) {
      const ok = await sendReply({
        requireAllAttachments: true,
        id: oid,
        comment,
        rasID: rg_id,
        parentCID: replyTarget.cid,
        replyCID: replyTarget.replyCid,
        objectType: 23,
        createdByUserId: created_by,
        taskId: tid,
        images,
        videos,
        audios,
        files,
        isPostCommnet: false,
        result_context_id,
        result_level,
        recurrence_ts,
      })
      if (ok) {
        dispatch(setCommentsCounter({ count: repliesCount + 1, key: replyTarget.cid, isReply: true }))
        dispatch(setCommentsCounter({ count: commentsCounter + 1, key: oid }))
        setReplyTarget((current) => (current === replyTarget ? null : current))
      }
      return !!ok
    }
    setUpdatedCommentsCounter(updatedCommentsCounter + 1)

    const isResponseOk = await sendComment({
      requireAllAttachments: splitView,
      id: oid,
      comment,
      rasID: rg_id,
      parentCID: parentCid,
      objectType: objectType,
      createdByUserId: created_by,
      result_context_id: result_context_id,
      taskId: tid,
      images,
      videos,
      audios,
      files,
      keyForSave,
      isPostCommnet: false,
      result_level: result_level,
      ...(recurrence_ts ? { recurrence_ts } : {}),
    })

    if (isResponseOk) dispatch(setCommentsCounter({ count: commentsCounter + 1, key: oid }))
    return !!isResponseOk
  }

  // ── Editor save: build PreviewFile list applying edits, then send comment ─
  const handleEditorSave = async (edits: Record<number, Blob>) => {
    if (editorImages.length === 0) {
      setEditorOpen(false)
      return
    }

    const finalFiles: PreviewFile[] = await Promise.all(
      editorImages.map(async (url, i) => {
        const blob = edits[i] || (await fetch(url).then((response) => response.blob()))

        return {
          file: new File([blob], `annotated_${i}.png`, { type: blob.type || 'image/png' }),
          previewUrl: URL.createObjectURL(blob),
        }
      })
    )

    try {
      const accepted = await handleCreateComment({ comment: editorCommentText, images: finalFiles })
      if (!accepted) return
      setEditorOpen(false)
      setEditorImages([])
      setEditorCommentText('')
      releaseAnnotationSource()
    } finally {
      finalFiles.forEach((file) => URL.revokeObjectURL(file.previewUrl))
    }
  }

  const handleEditorCancel = () => {
    setEditorOpen(false)
    setEditorImages([])
    setEditorCommentText('')
    releaseAnnotationSource()
  }

  // ── Comment slot for the editor ─────────────────────────────────────────
  const editorCommentSlot = (getPendingEdits: () => Record<number, Blob>) => (
    <Flex alignItems="center" style={{ columnGap: 8 }} w="100%" onClick={(e) => e.stopPropagation()}>
      <textarea
        placeholder="Write a comment…"
        value={editorCommentText}
        onChange={(e) => setEditorCommentText(e.target.value)}
        rows={1}
        style={{
          flex: 1,
          background: 'rgba(255,255,255,0.12)',
          color: '#F3F4F6',
          border: '1px solid rgba(255,255,255,0.2)',
          borderRadius: 8,
          padding: '8px 10px',
          fontSize: 14,
          resize: 'none',
          outline: 'none',
        }}
      />
      <Button
        type="button"
        variant="icon"
        aria-label={t.sendComment}
        onClick={() => handleEditorSave(getPendingEdits())}
        isLoading={sendCommentLoading}
        isDisabled={sendCommentLoading || editorImages.length === 0}
        w="38px"
        h="38px"
        borderRadius="50%"
        bg="#22272a"
        color="white"
        flexShrink={0}
        _hover={{ bg: '#374151' }}
      >
        <Send size={18} />
      </Button>
    </Flex>
  )

  const lastCommentId = comments[comments.length - 1]?.cid

  const autoScrollFile = useRef({ file: '', lastCommentId: '' })

  const saveCommentDraft = useCallback(
    (draft: ExecutionCommentDraft) => {
      drafts.current[keyForSave] = draft
    },
    [keyForSave, drafts]
  )

  const aiComments = comments.filter((c) => c.is_ai || c.created_by === AI_USER_ID)
  const scorePresentation = getAiScorePresentation(aiScore)
  const displayedComments = splitView ? [...aiComments, ...comments.filter((c) => !aiComments.includes(c))] : comments

  useEffect(() => {
    onCommentOverlayOpenChange?.(editorOpen || composerOverlayOpen)
    return () => onCommentOverlayOpenChange?.(false)
  }, [editorOpen, composerOverlayOpen, onCommentOverlayOpenChange])

  useLayoutEffect(() => {
    const viewport = scrollRef.current
    const dock = composerDockRef.current
    const panel = viewport?.parentElement

    if (!splitView || !viewport || !dock || !panel) return

    const measure = () => {
      const atBottom = viewport.scrollHeight - viewport.clientHeight - viewport.scrollTop < 24
      const height = `${Math.ceil(dock.getBoundingClientRect().height)}px`

      if (panel.style.getPropertyValue('--execution-composer-height') === height) return
      panel.style.setProperty('--execution-composer-height', height)
      if (atBottom) viewport.scrollTop = viewport.scrollHeight
    }

    measure()

    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)

    observer?.observe(dock)
    return () => observer?.disconnect()
  }, [splitView])

  useEffect(() => {
    if (!splitView || !replyTarget) return

    // Wait for the Reply banner and the dock's ResizeObserver to settle first.
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => {
        if (scrollRef.current && composerDockRef.current) {
          scrollReplyIntoView(scrollRef.current, composerDockRef.current)
        }
      })
    })

    return () => cancelAnimationFrame(frame)
  }, [splitView, replyTarget])

  useEffect(() => {
    if (drafts !== localDrafts) return
    const stored = drafts.current

    return () =>
      Object.values(stored).forEach((draft) =>
        draft.attachments.forEach((item) => URL.revokeObjectURL(item.previewUrl))
      )
  }, [drafts])

  useEffect(() => {
    if (comments.length === 0 && !targetCommentId) {
      getComments()
    }
    // eslint-disable-next-line
  }, [comments.length, keyForSave, targetCommentId])

  useEffect(() => {
    const targetTopLevelCommentId =
      targetParentCommentId && targetParentCommentId !== parentCid ? targetParentCommentId : targetCommentId

    if (!targetTopLevelCommentId || resolvedTargetWindowId === targetTopLevelCommentId) return
    if (comments.some((comment) => comment.cid === targetTopLevelCommentId)) {
      setResolvedTargetWindowId(targetTopLevelCommentId)
      return
    }

    let isMounted = true

    const fetchTargetWindow = async () => {
      await fetchComments({
        id: oid,
        cid: targetTopLevelCommentId,
        limit,
        created_ts: getCurrentEpoch(),
        parentCID: parentCid,
        keyForSave,
        includeCreatedAtTimestamp: true,
        queryOlderComments: true,
      })

      if (isMounted) {
        setResolvedTargetWindowId(targetTopLevelCommentId)
        setOffset(limit)
        setLimit(5)
      }
    }

    fetchTargetWindow()

    return () => {
      isMounted = false
    }
  }, [
    comments,
    fetchComments,
    keyForSave,
    limit,
    oid,
    parentCid,
    resolvedTargetWindowId,
    targetCommentId,
    targetParentCommentId,
  ])

  useEffect(() => {
    const viewedIds = comments.map((comment: IComment) => comment.cid)

    dispatch(
      updateViews({
        postID: oid,
        viewedIds: viewedIds,
      })
    )

    dispatch(
      setViewedCommentsIds({
        key: oid,
        ids: viewedIds,
      })
    )
  }, [comments, dispatch, oid])

  useEffect(() => {
    if (!splitView || targetCommentId || fetchCommentsLoading || !scrollRef.current) return
    if (autoScrollFile.current.file !== keyForSave || autoScrollFile.current.lastCommentId !== (lastCommentId || '')) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
      autoScrollFile.current = { file: keyForSave, lastCommentId: lastCommentId || '' }
    }
  }, [splitView, keyForSave, lastCommentId, fetchCommentsLoading, targetCommentId])

  useEffect(() => {
    setReplyTarget(null)
  }, [keyForSave])

  return (
    <Flex
      flexDirection="column"
      flexGrow={1}
      className={splitView ? 'comments-container execution-comments' : 'comments-container'}
      justifyContent="space-between"
      position="relative"
      h="100%"
      overflow="hidden"
    >
      <div
        ref={scrollRef}
        className={splitView ? 'execution-comments-scroll' : undefined}
        style={splitView ? undefined : { flex: 1, minHeight: 0, overflowY: 'auto' }}
        aria-busy={fetchCommentsLoading}
      >
        {!fetchCommentsLoading &&
          (splitView
            ? isLoadMore
            : isLoadMore && updatedCommentsCounter > 2 && comments.length < updatedCommentsCounter) && (
            <Button
              type="button"
              variant="ghost"
              onClick={getComments}
              isDisabled={fetchCommentsLoading}
              color={colors.greyDark}
              mb="5px"
            >
              <Text>
                <LocaleText text="fetch_more_comments_post_button" />
              </Text>
            </Button>
          )}

        {splitView && analysisLoading && <AIAnalysisCard loading />}
        {fetchCommentsLoading && <CommentsContentLoader count={comments.length ? limit : undefined} />}
        {splitView && !comments.length && !fetchCommentsLoading && (
          <div className="execution-discussion-empty">
            <MessageSquare size={24} />
            <strong>{t.noComments}</strong>
          </div>
        )}
        <>
          {!isEmpty(comments) && (
            <Flex overflowY="visible" px={splitView || isAIComment ? '0' : '16px'} flexDir="column">
              {displayedComments.map((comment: IComment) => (
                <div
                  key={comment.cid}
                  className={splitView && aiComments.includes(comment) ? 'execution-ai-message' : undefined}
                  style={
                    splitView && aiComments.includes(comment)
                      ? { backgroundColor: scorePresentation.colors.bg, borderColor: scorePresentation.colors.border }
                      : undefined
                  }
                >
                  {splitView && aiComments.includes(comment) && (
                    <div className="execution-ai-title" style={{ color: scorePresentation.colors.text }}>
                      ✦ {t.ai}{' '}
                      {scorePresentation.score !== undefined && (
                        <Chip
                          size="sm"
                          radius="7px"
                          color={scorePresentation.colors.text}
                          bg={scorePresentation.colors.bg}
                          border="0"
                          label={
                            <>
                              {t.score} <bdi>{scorePresentation.score}%</bdi>
                            </>
                          }
                        />
                      )}
                    </div>
                  )}
                  <Comment
                    key={comment.cid}
                    onRequestReply={splitView ? setReplyTarget : undefined}
                    isAIComment={splitView ? false : isAIComment}
                    {...comment}
                    activeReplyCommentId={
                      splitView && replyTarget?.cid === comment.cid
                        ? replyTarget.replyCid || replyTarget.cid
                        : undefined
                    }
                    fileDisplay={splitView ? 'cards' : 'links'}
                    rasID={rg_id}
                    taskId={tid}
                    postID={oid}
                    authorID={created_by}
                    isPostCommnet={false}
                    inputBgColor="white"
                    keyForSave={keyForSave}
                    targetParentCommentId={targetParentCommentId}
                    targetCommentId={targetCommentId}
                    recurrence_ts={recurrence_ts}
                    shouldOpenReplies={targetParentCommentId === comment.cid || replyTarget?.cid === comment.cid}
                  />
                </div>
              ))}
            </Flex>
          )}
        </>
      </div>
      <Box
        ref={composerDockRef}
        className={splitView ? 'execution-comment-input' : undefined}
        minH="55px"
        overflow={splitView ? 'visible' : 'hidden'}
        opacity={isMediaLoading ? 0.5 : 1}
        pointerEvents={splitView || isMediaLoading ? 'none' : 'auto'}
      >
        {splitView ? (
          composerHost &&
          createPortal(
            <ExecutionCommentComposer
              key={keyForSave}
              resetVersion={(review?.commentDraftResets || draftResets)[keyForSave] || 0}
              onSent={() =>
                review
                  ? review.resetCommentDraft(keyForSave)
                  : setDraftResets((versions) => ({ ...versions, [keyForSave]: (versions[keyForSave] || 0) + 1 }))
              }
              inlineAttachments={!!composerTarget}
              allowExpand={!composerTarget}
              onOverlayOpenChange={setComposerOverlayOpen}
              replyTarget={replyTarget}
              onCancelReply={() => setReplyTarget(null)}
              draft={drafts.current[keyForSave]}
              getCurrentDraft={() => drafts.current[keyForSave]}
              onDraftChange={saveCommentDraft}
              onSend={handleCreateComment}
              disabled={sendCommentLoading || sendingReply || isMediaLoading}
            />,
            composerHost
          )
        ) : (
          <CommentInput
            isFocused={false}
            postID={oid}
            handleCreateComment={async (data) => {
              await handleCreateComment(data)
            }}
            sendCommentLoading={sendCommentLoading}
            inputBgColor="white"
          />
        )}
      </Box>

      {/* ImageEditorModal for "Comment on image" flow */}
      <ImageEditorModal
        trapFocus={splitView}
        mode="comment"
        isOpen={editorOpen}
        images={editorImages}
        hasLocalImages={editorImages.length > 0}
        onSave={handleEditorSave}
        onCancel={handleEditorCancel}
        commentSlot={editorCommentSlot}
      />
    </Flex>
  )
}

export default CommentSection
